/**
 * Learned Locations Admin Service
 * Service for managing learned locations in the admin dashboard
 */

import { supabase } from './customSupabaseClient.js';

class LearnedLocationsAdminService {
  constructor() {
    this.tableName = 'learned_locations';
  }

  /**
   * Get all learned locations with pagination and filtering
   */
  async getLearnedLocations(options = {}) {
    try {
      const {
        page = 1,
        limit = 50,
        search = '',
        sortBy = 'last_searched_at',
        sortOrder = 'desc',
        isActive = null
      } = options;

      let query = supabase
        .from(this.tableName)
        .select('*', { count: 'exact' });

      // Apply search filter
      if (search) {
        query = query.or(`name.ilike.%${search}%,address.ilike.%${search}%,search_key.ilike.%${search}%`);
      }

      // Apply active filter
      if (isActive !== null) {
        query = query.eq('is_active', isActive);
      }

      // Apply sorting
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      // Apply pagination
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        console.error('Error fetching learned locations:', error);
        return { data: [], count: 0, error };
      }

      return { data: data || [], count: count || 0, error: null };
    } catch (error) {
      console.error('Error in getLearnedLocations:', error);
      return { data: [], count: 0, error };
    }
  }

  /**
   * Get learned locations statistics
   */
  async getLearnedLocationsStats() {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .select('is_active, search_count, created_at, last_searched_at');

      if (error) {
        console.error('Error fetching learned locations stats:', error);
        return this.getDefaultStats();
      }

      const locations = data || [];
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      const stats = {
        total: locations.length,
        active: locations.filter(loc => loc.is_active).length,
        inactive: locations.filter(loc => !loc.is_active).length,
        recent: locations.filter(loc => new Date(loc.last_searched_at) > sevenDaysAgo).length,
        totalSearches: locations.reduce((sum, loc) => sum + (loc.search_count || 0), 0),
        avgSearchesPerLocation: locations.length > 0 ? 
          locations.reduce((sum, loc) => sum + (loc.search_count || 0), 0) / locations.length : 0,
        mostSearched: locations.length > 0 ? 
          locations.reduce((max, loc) => (loc.search_count || 0) > (max.search_count || 0) ? loc : max) : null,
        recentlyAdded: locations.filter(loc => new Date(loc.created_at) > thirtyDaysAgo).length
      };

      return stats;
    } catch (error) {
      console.error('Error in getLearnedLocationsStats:', error);
      return this.getDefaultStats();
    }
  }

  /**
   * Get default stats when database is not available
   */
  getDefaultStats() {
    return {
      total: 0,
      active: 0,
      inactive: 0,
      recent: 0,
      totalSearches: 0,
      avgSearchesPerLocation: 0,
      mostSearched: null,
      recentlyAdded: 0
    };
  }

  /**
   * Update a learned location
   */
  async updateLearnedLocation(id, updates) {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select();

      if (error) {
        console.error('Error updating learned location:', error);
        return { data: null, error };
      }

      return { data: data?.[0] || null, error: null };
    } catch (error) {
      console.error('Error in updateLearnedLocation:', error);
      return { data: null, error };
    }
  }

  /**
   * Delete a learned location
   */
  async deleteLearnedLocation(id) {
    try {
      const { error } = await supabase
        .from(this.tableName)
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting learned location:', error);
        return { error };
      }

      return { error: null };
    } catch (error) {
      console.error('Error in deleteLearnedLocation:', error);
      return { error };
    }
  }

  /**
   * Bulk update learned locations
   */
  async bulkUpdateLearnedLocations(updates) {
    try {
      const promises = updates.map(update => 
        this.updateLearnedLocation(update.id, update.data)
      );

      const results = await Promise.all(promises);
      const errors = results.filter(result => result.error);
      
      return {
        success: results.length - errors.length,
        errors: errors.length,
        results
      };
    } catch (error) {
      console.error('Error in bulkUpdateLearnedLocations:', error);
      return { success: 0, errors: updates.length, results: [] };
    }
  }

  /**
   * Get learned locations by category (based on search patterns)
   */
  async getLearnedLocationsByCategory() {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .select('name, address, search_count, is_active');

      if (error) {
        console.error('Error fetching learned locations by category:', error);
        return {};
      }

      const locations = data || [];
      const categories = {
        airports: locations.filter(loc => 
          loc.name.toLowerCase().includes('airport') || 
          loc.address.toLowerCase().includes('airport')
        ),
        hotels: locations.filter(loc => 
          loc.name.toLowerCase().includes('hotel') || 
          loc.address.toLowerCase().includes('hotel')
        ),
        restaurants: locations.filter(loc => 
          loc.name.toLowerCase().includes('restaurant') || 
          loc.name.toLowerCase().includes('cafe') ||
          loc.name.toLowerCase().includes('dining')
        ),
        universities: locations.filter(loc => 
          loc.name.toLowerCase().includes('university') || 
          loc.name.toLowerCase().includes('college') ||
          loc.address.toLowerCase().includes('university')
        ),
        hospitals: locations.filter(loc => 
          loc.name.toLowerCase().includes('hospital') || 
          loc.name.toLowerCase().includes('medical') ||
          loc.address.toLowerCase().includes('hospital')
        ),
        shopping: locations.filter(loc => 
          loc.name.toLowerCase().includes('mall') || 
          loc.name.toLowerCase().includes('shopping') ||
          loc.name.toLowerCase().includes('center')
        ),
        other: locations.filter(loc => {
          const name = loc.name.toLowerCase();
          const address = loc.address.toLowerCase();
          return !name.includes('airport') && !name.includes('hotel') && 
                 !name.includes('restaurant') && !name.includes('cafe') &&
                 !name.includes('university') && !name.includes('college') &&
                 !name.includes('hospital') && !name.includes('medical') &&
                 !name.includes('mall') && !name.includes('shopping') &&
                 !name.includes('center');
        })
      };

      return categories;
    } catch (error) {
      console.error('Error in getLearnedLocationsByCategory:', error);
      return {};
    }
  }
}

export default new LearnedLocationsAdminService();
